/**
 * currencyUtils.js — display helpers for CAD storefront prices.
 * Reference currencies: US → USD; UK → GBP; Australia → AUD; other INTL → EUR.
 */

import { resolveCountryCode } from './addressUtils';
import { DEFAULT_CURRENCY_RATES } from '../hooks/useCurrencyRates';

export const CAD_TO_USD_RATE = DEFAULT_CURRENCY_RATES.cadToUsdRate;
export const CAD_TO_EUR_RATE = DEFAULT_CURRENCY_RATES.cadToEurRate;
export const CAD_TO_GBP_RATE = DEFAULT_CURRENCY_RATES.cadToGbpRate;
export const CAD_TO_AUD_RATE = DEFAULT_CURRENCY_RATES.cadToAudRate;

const GBP_COUNTRY_KEYS = new Set([
  'united kingdom',
  'uk',
  'great britain',
  'england',
  'scotland',
  'wales',
  'northern ireland',
  'britain',
]);

const AUD_COUNTRY_KEYS = new Set(['australia']);

function normalizeCountryKey(name = '') {
  return String(name || '').trim().toLowerCase();
}

/** Normalize string countryCode or address-like object for currency helpers. */
export function normalizeCurrencyContext(input, countryName = '') {
  if (input && typeof input === 'object' && !Array.isArray(input)) {
    return {
      countryCode: resolveCountryCode(input),
      countryName: String(input.countryName || input.country || '').trim(),
    };
  }
  return {
    countryCode: input || 'CA',
    countryName: String(countryName || '').trim(),
  };
}

/** Reference currency code for display, or null for Canada (CAD only). */
export function getReferenceCurrency(countryCode, countryName = '') {
  const ctx = normalizeCurrencyContext(countryCode, countryName);
  if (ctx.countryCode === 'US') return 'USD';
  if (ctx.countryCode !== 'INTL') return null;

  const key = normalizeCountryKey(ctx.countryName);
  if (GBP_COUNTRY_KEYS.has(key)) return 'GBP';
  if (AUD_COUNTRY_KEYS.has(key)) return 'AUD';
  return 'EUR';
}

export function getDisplayCurrency(countryCode, countryName = '') {
  return getReferenceCurrency(countryCode, countryName) || 'CAD';
}

export function convertCadCentsToUsd(cadCents, rates = DEFAULT_CURRENCY_RATES) {
  if (!Number.isFinite(cadCents)) return 0;
  return (cadCents / 100) * (rates.cadToUsdRate ?? DEFAULT_CURRENCY_RATES.cadToUsdRate);
}

export function convertCadCentsToEur(cadCents, rates = DEFAULT_CURRENCY_RATES) {
  if (!Number.isFinite(cadCents)) return 0;
  return (cadCents / 100) * (rates.cadToEurRate ?? DEFAULT_CURRENCY_RATES.cadToEurRate);
}

export function convertCadCentsToGbp(cadCents, rates = DEFAULT_CURRENCY_RATES) {
  if (!Number.isFinite(cadCents)) return 0;
  return (cadCents / 100) * (rates.cadToGbpRate ?? DEFAULT_CURRENCY_RATES.cadToGbpRate);
}

export function convertCadCentsToAud(cadCents, rates = DEFAULT_CURRENCY_RATES) {
  if (!Number.isFinite(cadCents)) return 0;
  return (cadCents / 100) * (rates.cadToAudRate ?? DEFAULT_CURRENCY_RATES.cadToAudRate);
}

function formatReferenceAmount(cadCents, refCurrency, rates) {
  switch (refCurrency) {
    case 'USD':
      return `~$${convertCadCentsToUsd(cadCents, rates).toFixed(2)} USD`;
    case 'GBP':
      return `~£${convertCadCentsToGbp(cadCents, rates).toFixed(2)} GBP`;
    case 'AUD':
      return `~$${convertCadCentsToAud(cadCents, rates).toFixed(2)} AUD`;
    case 'EUR':
    default:
      return `~€${convertCadCentsToEur(cadCents, rates).toFixed(2)} EUR`;
  }
}

/** True when cart/checkout should show CAD + a reference currency. */
export function showsReferenceCurrency(countryCode, countryName = '') {
  return getReferenceCurrency(countryCode, countryName) !== null;
}

/**
 * Format a CAD-cent amount for display.
 * CA → CAD only; others should prefer formatDualCurrency for totals.
 */
export function formatCartMoney(cadCents, countryCode, { showCode = true } = {}) {
  if (!Number.isFinite(cadCents)) return '—';

  const amount = cadCents / 100;
  const formatted = amount.toFixed(2);

  return showCode ? `$${formatted} CAD` : `$${formatted}`;
}

/**
 * CAD charge amount plus approximate reference currency for the customer's region.
 * Returns null for Canadian customers.
 */
export function formatDualCurrency(cadCents, countryCode, rates = DEFAULT_CURRENCY_RATES, countryName = '') {
  const ctx = normalizeCurrencyContext(countryCode, countryName);
  const refCurrency = getReferenceCurrency(ctx.countryCode, ctx.countryName);
  if (!Number.isFinite(cadCents) || !refCurrency) return null;

  const cad = `$${(cadCents / 100).toFixed(2)} CAD`;
  return {
    cad,
    secondary: formatReferenceAmount(cadCents, refCurrency, rates),
    refCurrency,
  };
}

/** Short footnote for cart/checkout when a reference currency is shown. */
export function referenceCurrencyNote(countryCode, rates = DEFAULT_CURRENCY_RATES, countryName = '') {
  const ctx = normalizeCurrencyContext(countryCode, countryName);
  const refCurrency = getReferenceCurrency(ctx.countryCode, ctx.countryName);
  if (!refCurrency) return null;

  if (refCurrency === 'USD') {
    const usdPerCad = rates.cadToUsdRate ?? DEFAULT_CURRENCY_RATES.cadToUsdRate;
    return `Charged in CAD. USD shown for reference at approx. ${(usdPerCad * 100).toFixed(0)}¢ per CAD.`;
  }
  if (refCurrency === 'GBP') {
    const gbpPerCad = rates.cadToGbpRate ?? DEFAULT_CURRENCY_RATES.cadToGbpRate;
    return `Charged in CAD. GBP shown for reference at approx. £${gbpPerCad.toFixed(2)} per CAD.`;
  }
  if (refCurrency === 'AUD') {
    const audPerCad = rates.cadToAudRate ?? DEFAULT_CURRENCY_RATES.cadToAudRate;
    return `Charged in CAD. AUD shown for reference at approx. $${audPerCad.toFixed(2)} per CAD.`;
  }
  const eurPerCad = rates.cadToEurRate ?? DEFAULT_CURRENCY_RATES.cadToEurRate;
  return `Charged in CAD. EUR shown for reference at approx. €${eurPerCad.toFixed(2)} per CAD.`;
}
